/**
 * PoolReassignDialog — transférer une réservation confirmée à un autre agent.
 * =============================================================================
 * Lot 2.4 « le temps » (Q5 de l'audit CD31, « changement d'agent ») : un
 * conducteur est malade, en réunion, muté — la réservation ne se perd pas, elle
 * change de titulaire. Ouvert depuis le détail d'un bloc de la grille hebdo
 * (`PoolScheduleGrid`), sur un bloc `reserved` SEULEMENT : un bloc `out` est
 * une garde en cours, et une passation n'est pas un transfert — elle se rend,
 * puis se re-réserve. Le serveur le refuse de toute façon en `BAD_STATE`.
 *
 * Contrat : `poolManagerReservationAPI.reassign` (`api/pool.js`) →
 * `POST /pool/reservations/:id/reassign` → `backend/modules/pool/reassign_service.mjs`.
 * Refus lisibles ici : `DRIVER_BUSY` (le nouvel agent est déjà pris sur le
 * créneau), `BAD_STATE`, 404 hors périmètre — même contrat d'erreur (`message`,
 * `code`, `next_actions`) que le reste du module, rendu comme le fait
 * `PoolReserveForDriverDialog`.
 *
 * La liste des conducteurs est chargée et filtrée sur le MÊME patron que
 * `PoolReserveForDriverDialog` (`driverAPI.getAll`, recherche insensible aux
 * accents, rendu borné à `MAX_RENDERED_OPTIONS`) : une recherche qui ne laisse
 * qu'un candidat le choisit. Le titulaire actuel est retiré de la liste — le
 * serveur refuserait « déjà le titulaire », autant ne pas le proposer.
 *
 * Limite connue : la liste n'est pas filtrée par disponibilité sur le créneau.
 * Le refus `DRIVER_BUSY` du serveur reste le juge, et il est affiché tel quel.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { X, User, AlertTriangle, Search } from 'lucide-react';
import { poolManagerReservationAPI } from '../../api/pool.js';
import { driverAPI } from '../../api/drivers.js';

/** Au-delà, on ne rend rien de plus : mille `<option>` ne se lisent pas, elles se cherchent. */
const MAX_RENDERED_OPTIONS = 200;

const driverLabel = (d) => [d.first_name, d.last_name].filter(Boolean).join(' ').trim() || `#${d.id}`;
const normalize = (s) => String(s ?? '').toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '');
const matches = (haystack, needle) => normalize(haystack).includes(normalize(needle));

/**
 * @param {Object} props
 * @param {number|string} props.reservationId
 * @param {number|string|null} [props.currentDriverId] - titulaire actuel, retiré de la liste
 * @param {string|null} [props.currentDriverLabel] - son nom, pour le sous-titre
 * @param {string} [props.summary] - le véhicule et le créneau, déjà formatés par l'appelant
 * @param {number|string|null} [props.siteId] - filtre de convenance (l'autorité reste côté serveur)
 * @param {() => void} props.onClose
 * @param {(reservation: object) => void} [props.onReassigned]
 */
function PoolReassignDialog({ reservationId, currentDriverId = null, currentDriverLabel = null, summary = '', siteId = null, onClose, onReassigned }) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const [driverId, setDriverId] = useState('');
  const [driverSearch, setDriverSearch] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);

  // Même clé que `PoolReserveForDriverDialog` : une seule liste en cache pour les deux dialogues.
  const driversQuery = useQuery({
    queryKey: ['pool', 'reserve-for-driver', 'drivers'],
    queryFn: () => driverAPI.getAll(),
    staleTime: 60_000,
  });

  const drivers = useMemo(() => {
    const res = driversQuery.data;
    return res?.data || (Array.isArray(res) ? res : []);
  }, [driversQuery.data]);

  const candidates = useMemo(() => {
    const scoped = siteId ? drivers.filter((d) => String(d.working_site_id) === String(siteId)) : drivers;
    return currentDriverId == null ? scoped : scoped.filter((d) => String(d.id) !== String(currentDriverId));
  }, [drivers, siteId, currentDriverId]);

  const filteredDrivers = useMemo(() => {
    const q = driverSearch.trim();
    if (!q) return candidates;
    return candidates.filter((d) => matches(driverLabel(d), q));
  }, [candidates, driverSearch]);

  useEffect(() => {
    if (driverSearch.trim() && filteredDrivers.length === 1) setDriverId(String(filteredDrivers[0].id));
  }, [driverSearch, filteredDrivers]);

  const shownDrivers = filteredDrivers.slice(0, MAX_RENDERED_OPTIONS);
  const canSubmit = Boolean(driverId) && !submitting;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!driverId) return;
    setSubmitError(null);
    setSubmitting(true);
    try {
      const res = await poolManagerReservationAPI.reassign(reservationId, driverId);
      const reservation = res?.data?.reservation || res?.reservation || res?.data || null;
      // Le bloc de la grille et les fiches doivent changer de nom TOUT DE SUITE,
      // pas au prochain tic de 60 s.
      queryClient.invalidateQueries({ queryKey: ['pool-schedule'] });
      queryClient.invalidateQueries({ queryKey: ['poolPresence'] });
      onReassigned?.(reservation);
      onClose();
    } catch (err) {
      // `core.js` garde le message du module (`err.message`) et ses `next_actions`.
      const message = err.message || t('poolReassign.errors.generic');
      const nextActions = err.next_actions || err.data?.next_actions || null;
      setSubmitError({ message, code: err.code || null, nextActions });
    } finally {
      setSubmitting(false);
    }
  };

  const searchInputClass = 'w-full rounded-lg border border-gray-300 py-1.5 pl-8 pr-3 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500';
  const selectClass = 'w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="max-h-[90vh] w-[460px] max-w-full overflow-y-auto rounded-xl bg-white shadow-2xl" role="dialog" aria-label={t('poolReassign.title')}>
        <div className="flex items-center justify-between rounded-t-xl bg-gradient-to-r from-blue-600 to-blue-500 px-6 py-4">
          <div className="flex items-center gap-2">
            <User className="h-5 w-5 text-white" />
            <h2 className="text-lg font-semibold text-white">{t('poolReassign.title')}</h2>
          </div>
          <button type="button" onClick={onClose} className="text-white/80 hover:text-white" aria-label={t('common.actions.close')}>
            <X className="h-5 w-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4 p-6">
          {summary && <p className="text-sm font-medium text-gray-900">{summary}</p>}
          <p className="text-sm text-gray-500">
            {currentDriverLabel
              ? t('poolReassign.subtitleFrom', { name: currentDriverLabel })
              : t('poolReassign.subtitle')}
          </p>

          {driversQuery.error && (
            <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
              {driversQuery.error.message}
            </div>
          )}

          <div>
            <span className="mb-1 flex items-center gap-1.5 text-xs font-medium text-gray-600">
              <User className="h-3.5 w-3.5" />
              {t('poolReassign.fields.newDriver')}
            </span>
            <div className="relative mb-1.5">
              <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                value={driverSearch}
                onChange={(e) => setDriverSearch(e.target.value)}
                placeholder={t('poolReassign.fields.driverSearchPlaceholder')}
                aria-label={t('poolReassign.fields.driverSearchPlaceholder')}
                className={searchInputClass}
              />
            </div>
            <select
              value={driverId}
              onChange={(e) => setDriverId(e.target.value)}
              disabled={driversQuery.isLoading}
              required
              aria-label={t('poolReassign.fields.newDriver')}
              className={selectClass}
            >
              <option value="">{t('poolReassign.fields.driverPlaceholder')}</option>
              {shownDrivers.map((d) => (
                <option key={d.id} value={d.id}>{driverLabel(d)}</option>
              ))}
            </select>
            {filteredDrivers.length > MAX_RENDERED_OPTIONS && (
              <p className="mt-1 text-xs text-amber-700">
                {t('poolReassign.hints.tooManyOptions', { shown: MAX_RENDERED_OPTIONS, total: filteredDrivers.length })}
              </p>
            )}
          </div>

          {submitError && (
            <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700" role="alert">
              <p className="flex items-start gap-1.5">
                <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
                <span>
                  <span className="font-medium">{t('poolReassign.refused.title')}</span> {submitError.message}
                </span>
              </p>
              {Array.isArray(submitError.nextActions) && submitError.nextActions.length > 0 && (
                <ul className="mt-1.5 list-inside list-disc space-y-0.5 pl-1">
                  {submitError.nextActions.map((a, i) => (
                    <li key={i}>{a.label || a.code || a.kind}</li>
                  ))}
                </ul>
              )}
            </div>
          )}

          <div className="flex justify-end gap-2 border-t border-gray-200 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
            >
              {t('common.actions.cancel')}
            </button>
            <button
              type="submit"
              disabled={!canSubmit}
              className="rounded-lg bg-blue-600 px-4 py-2 text-sm text-white hover:bg-blue-700 disabled:cursor-not-allowed disabled:bg-gray-300"
            >
              {submitting ? t('poolReassign.actions.submitting') : t('poolReassign.actions.submit')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default PoolReassignDialog;
